/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.reminders

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.PaymentRepository
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderSentEntry
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.formatter.RenderMessageTemplateUseCase
import studio.oddpods.cauce.core.model.payment.CreatePaymentRequest
import studio.oddpods.cauce.core.model.payment.PaymentMethod
import studio.oddpods.cauce.core.model.reminder.PendingReminder
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.reminders.generated.resources.Res
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_error_launch_failed
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_error_load_failed
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_error_record_failed
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_mark_paid_error
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_mark_paid_success_one
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_sent_success
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_sent_success_not_recorded

data class ConfirmDialogData(
    val patientId: String,
    val patientName: String,
    val patientPhone: String,
    val messageText: String,
    val entries: List<ReminderSentEntry>,
)

data class MarkPaidSessionSelection(
    val sessionId: String,
    val sessionDateIso: String?,
    val formattedDate: String?,
    val amountOwed: Long,
    val selected: Boolean = true,
)

data class MarkPaidDialogData(
    val patientId: String,
    val patientName: String,
    val sessions: List<MarkPaidSessionSelection>,
    val method: PaymentMethod = PaymentMethod.CASH,
)

data class RemindersState(
    val pending: List<PendingReminder> = emptyList(),
    val filterPatientId: String? = null,
    val isLoading: Boolean = true,
    val isTemplateReady: Boolean = false,
    val loadError: UiText? = null,
    val sending: Boolean = false,
    val sendError: UiText? = null,
    val snackbar: UiText? = null,
    val confirmDialog: ConfirmDialogData? = null,
    val markPaidDialog: MarkPaidDialogData? = null,
    val markingPaid: Boolean = false,
)

sealed class RemindersEvent

sealed class RemindersAction {
    data class SetFilter(val patientId: String?) : RemindersAction()
    data class SendClicked(val patientId: String) : RemindersAction()
    data class ConfirmSend(val dialog: ConfirmDialogData) : RemindersAction()
    data object DismissDialog : RemindersAction()
    data object DismissSnackbar : RemindersAction()
    data class MarkAsPaidClicked(val patientId: String) : RemindersAction()
    data class MarkPaidSessionToggled(val sessionId: String) : RemindersAction()
    data class MarkPaidMethodChanged(val method: PaymentMethod) : RemindersAction()
    data object ConfirmMarkPaid : RemindersAction()
    data object DismissMarkPaidDialog : RemindersAction()
}

class RemindersViewModel(
    private val reminderRepository: ReminderRepository,
    private val profileRepository: ProfileRepository,
    private val launchWhatsApp: suspend (phoneE164: String, message: String) -> Boolean,
    private val renderTemplate: RenderMessageTemplateUseCase,
    private val paymentRepository: PaymentRepository,
    private val dateFormatter: DateFormatter,
) : BaseViewModel<RemindersState, RemindersEvent, RemindersAction>(RemindersState()) {

    private var template: String = ""

    init {
        observe()
    }

    override fun handleAction(action: RemindersAction) {
        when (action) {
            is RemindersAction.SetFilter ->
                mutableStateFlow.value = state.copy(filterPatientId = action.patientId)
            is RemindersAction.SendClicked -> onSendClicked(action.patientId)
            is RemindersAction.ConfirmSend -> onConfirmSend(action.dialog)
            RemindersAction.DismissDialog ->
                mutableStateFlow.value = state.copy(confirmDialog = null)
            RemindersAction.DismissSnackbar ->
                mutableStateFlow.value = state.copy(snackbar = null)
            is RemindersAction.MarkAsPaidClicked -> onMarkAsPaidClicked(action.patientId)
            is RemindersAction.MarkPaidSessionToggled -> {
                val dialog = state.markPaidDialog ?: return
                mutableStateFlow.value = state.copy(
                    markPaidDialog = dialog.copy(
                        sessions = dialog.sessions.map { s ->
                            if (s.sessionId == action.sessionId) s.copy(selected = !s.selected) else s
                        },
                    ),
                )
            }
            is RemindersAction.MarkPaidMethodChanged -> {
                val dialog = state.markPaidDialog ?: return
                mutableStateFlow.value = state.copy(
                    markPaidDialog = dialog.copy(method = action.method),
                )
            }
            RemindersAction.ConfirmMarkPaid -> onConfirmMarkPaid()
            RemindersAction.DismissMarkPaidDialog ->
                mutableStateFlow.value = state.copy(markPaidDialog = null)
        }
    }

    private fun observe() {
        viewModelScope.launch {
            profileRepository.getCurrentProfile().onSuccess { profile ->
                template = profile.reminderTemplate
                mutableStateFlow.value = state.copy(isTemplateReady = true)
            }
        }
        viewModelScope.launch {
            reminderRepository.getPendingReminders()
                .catch { _ ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        loadError = UiText.Resource(Res.string.feature_reminders_error_load_failed),
                    )
                }
                .collect { list ->
                    mutableStateFlow.value = state.copy(pending = list, isLoading = false)
                }
        }
    }

    private fun onSendClicked(patientId: String) {
        // Guard: don't build a dialog before the profile template has loaded, otherwise
        // the user could send a blank WhatsApp message while we still record the reminder.
        if (!state.isTemplateReady) return
        val patient = state.pending.firstOrNull { it.patientId == patientId } ?: return
        val text = renderTemplate.invoke(template, patient)
        val entries = patient.dueSessions.map {
            ReminderSentEntry(it.sessionId, it.nextReminderNumber)
        }
        mutableStateFlow.value = state.copy(
            confirmDialog = ConfirmDialogData(
                patientId = patient.patientId,
                patientName = patient.patientName,
                patientPhone = patient.patientPhone,
                messageText = text,
                entries = entries,
            ),
        )
    }

    private fun onConfirmSend(dialog: ConfirmDialogData) {
        mutableStateFlow.value = state.copy(sending = true, sendError = null)
        viewModelScope.launch {
            val launched = launchWhatsApp(dialog.patientPhone, dialog.messageText)
            if (!launched) {
                mutableStateFlow.value = state.copy(
                    sending = false,
                    confirmDialog = null,
                    sendError = UiText.Resource(Res.string.feature_reminders_error_launch_failed),
                )
                return@launch
            }
            val recordError: UiText? = reminderRepository.recordBatchSent(dialog.entries)
                .fold(
                    onSuccess = { null },
                    onFailure = { it.toUiText(Res.string.feature_reminders_error_record_failed) },
                )
            mutableStateFlow.value = state.copy(
                sending = false,
                confirmDialog = null,
                snackbar = if (recordError == null) {
                    UiText.Resource(Res.string.feature_reminders_sent_success)
                } else {
                    UiText.Resource(Res.string.feature_reminders_sent_success_not_recorded)
                },
            )
        }
    }

    private fun onMarkAsPaidClicked(patientId: String) {
        if (state.markingPaid) return
        val patient = state.pending.firstOrNull { it.patientId == patientId } ?: return
        mutableStateFlow.value = state.copy(
            markPaidDialog = MarkPaidDialogData(
                patientId = patient.patientId,
                patientName = patient.patientName,
                sessions = patient.dueSessions.map { session ->
                    MarkPaidSessionSelection(
                        sessionId = session.sessionId,
                        sessionDateIso = session.sessionDate,
                        formattedDate = dateFormatter.format(session.sessionDate),
                        amountOwed = session.amountOwed,
                    )
                },
            ),
        )
    }

    private fun onConfirmMarkPaid() {
        val dialog = state.markPaidDialog ?: return
        val selected = dialog.sessions.filter { it.selected }
        if (selected.isEmpty()) {
            mutableStateFlow.value = state.copy(markPaidDialog = null)
            return
        }
        mutableStateFlow.value = state.copy(markingPaid = true)
        viewModelScope.launch {
            val todayIso = kotlin.time.Clock.System.now()
                .toLocalDateTime(TimeZone.currentSystemDefault())
                .date.toString()
            var failed = false
            for (s in selected) {
                val result = paymentRepository.createPayment(
                    CreatePaymentRequest(
                        sessionId = s.sessionId,
                        patientId = dialog.patientId,
                        amount = s.amountOwed,
                        method = dialog.method.name,
                        notes = null,
                        receivedAt = todayIso,
                    ),
                )
                if (result.isFailure) {
                    failed = true
                    break
                }
            }
            if (failed) {
                mutableStateFlow.value = state.copy(
                    markingPaid = false,
                    snackbar = UiText.Resource(Res.string.feature_reminders_mark_paid_error),
                )
                return@launch
            }
            reminderRepository.refresh()
            val successMsg = if (selected.size == 1) {
                UiText.Resource(Res.string.feature_reminders_mark_paid_success_one)
            } else {
                UiText.Dynamic("${selected.size} pagos registrados")
            }
            mutableStateFlow.value = state.copy(
                markingPaid = false,
                markPaidDialog = null,
                snackbar = successMsg,
            )
        }
    }
}
